var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
var ToastContext = createContext(undefined);
export var useToast = function () {
    var context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
var generateToastId = function () { return "toast_".concat(Date.now(), "_").concat(Math.random().toString(36).substr(2, 9)); };
export var ToastProvider = function (_a) {
    var children = _a.children;
    var _b = useState([]), toasts = _b[0], setToasts = _b[1];
    var removeToast = useCallback(function (id) {
        setToasts(function (prev) { return prev.filter(function (toast) { return toast.id !== id; }); });
    }, []);
    var addToast = useCallback(function (toast) {
        var _a, _b;
        var id = generateToastId();
        var newToast = __assign(__assign({}, toast), { id: id, duration: (_a = toast.duration) !== null && _a !== void 0 ? _a : 5000, dismissible: (_b = toast.dismissible) !== null && _b !== void 0 ? _b : true });
        setToasts(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newToast], false); });
        // Auto-remove after duration (if duration > 0)
        if (newToast.duration && newToast.duration > 0) {
            setTimeout(function () { return removeToast(id); }, newToast.duration);
        }
        return id;
    }, [removeToast]);
    var clearAll = useCallback(function () {
        setToasts([]);
    }, []);
    var showError = useCallback(function (error, action) {
        var message = typeof error === 'string' ? error : error.message;
        var appError = typeof error === 'string' ? undefined : error;
        return addToast({
            message: message,
            type: 'error',
            action: action,
            appError: appError,
            duration: (appError === null || appError === void 0 ? void 0 : appError.retryable) ? 8000 : 6000 // Longer duration for retryable errors
        });
    }, [addToast]);
    var showSuccess = useCallback(function (message, action) {
        return addToast({
            message: message,
            type: 'success',
            action: action,
            duration: 4000
        });
    }, [addToast]);
    var showWarning = useCallback(function (message, action) {
        return addToast({
            message: message,
            type: 'warning',
            action: action,
            duration: 6000
        });
    }, [addToast]);
    var showInfo = useCallback(function (message, action) {
        return addToast({
            message: message,
            type: 'info',
            action: action,
            duration: 5000
        });
    }, [addToast]);
    var value = {
        toasts: toasts,
        addToast: addToast,
        removeToast: removeToast,
        clearAll: clearAll,
        showError: showError,
        showSuccess: showSuccess,
        showWarning: showWarning,
        showInfo: showInfo
    };
    return (_jsxs(ToastContext.Provider, __assign({ value: value }, { children: [children, _jsx(ToastContainer, { toasts: toasts, onRemove: removeToast })] })));
};
var ToastContainer = function (_a) {
    var toasts = _a.toasts, onRemove = _a.onRemove;
    // Create portal to render toasts at the top level
    return createPortal(_jsx("div", __assign({ className: "fixed top-4 right-4 z-50 space-y-3 max-w-sm w-full" }, { children: toasts.map(function (toast) { return (_jsx(ToastComponent, { toast: toast, onRemove: onRemove }, toast.id)); }) })), document.body);
};
var ToastComponent = function (_a) {
    var _b;
    var toast = _a.toast, onRemove = _a.onRemove;
    var _c = useState(false), isVisible = _c[0], setIsVisible = _c[1];
    var _d = useState(false), isExiting = _d[0], setIsExiting = _d[1];
    useEffect(function () {
        // Trigger entrance animation
        var timer = setTimeout(function () { return setIsVisible(true); }, 10);
        return function () { return clearTimeout(timer); };
    }, []);
    var handleClose = function () {
        if (!isExiting) {
            setIsExiting(true);
            setTimeout(function () { return onRemove(toast.id); }, 300);
        }
    };
    var getIcon = function () {
        switch (toast.type) {
            case 'success':
                return '🎉';
            case 'warning':
                return '⚠️';
            case 'error':
                return '😅';
            case 'info':
                return 'ℹ️';
            default:
                return '📢';
        }
    };
    var getBackgroundClass = function () {
        var baseClass = 'backdrop-blur-md border shadow-lg';
        switch (toast.type) {
            case 'success':
                return "".concat(baseClass, " bg-success-muted/90 dark:bg-success-muted/20 border-success/20 dark:border-success");
            case 'warning':
                return "".concat(baseClass, " bg-warning-muted/90 dark:bg-warning-muted/20 border-warning/20 dark:border-warning");
            case 'error':
                return "".concat(baseClass, " bg-error-muted/90 dark:bg-error-muted/20 border-error/20 dark:border-error");
            case 'info':
                return "".concat(baseClass, " bg-info-muted/90 dark:bg-info-muted/20 border-info/20 dark:border-info");
            default:
                return "".concat(baseClass, " bg-muted/90 dark:bg-muted/20 border-border dark:border-border");
        }
    };
    var getTextClass = function () {
        switch (toast.type) {
            case 'success':
                return 'text-success dark:text-success';
            case 'warning':
                return 'text-warning dark:text-warning';
            case 'error':
                return 'text-error dark:text-error';
            case 'info':
                return 'text-info dark:text-info';
            default:
                return 'text-foreground dark:text-muted-foreground';
        }
    };
    return (_jsx("div", __assign({ className: "\n        ".concat(getBackgroundClass(), "\n        rounded-lg p-4 transition-all duration-300 ease-out\n        ").concat(isVisible && !isExiting ? 'transform translate-x-0 opacity-100' : 'transform translate-x-full opacity-0', "\n        ").concat(isExiting ? 'scale-95' : '', "\n      ") }, { children: _jsxs("div", __assign({ className: "flex items-start space-x-3" }, { children: [_jsx("span", __assign({ className: "text-lg flex-shrink-0 mt-0.5" }, { children: getIcon() })), _jsxs("div", __assign({ className: "flex-1 min-w-0" }, { children: [_jsx("p", __assign({ className: "text-sm font-medium ".concat(getTextClass()) }, { children: toast.message })), ((_b = toast.appError) === null || _b === void 0 ? void 0 : _b.retryable) && (_jsx("div", __assign({ className: "mt-2" }, { children: _jsx("button", __assign({ onClick: function () {
                                    // Emit custom retry event
                                    window.dispatchEvent(new CustomEvent('retryError', {
                                        detail: { appError: toast.appError }
                                    }));
                                    handleClose();
                                }, className: "text-xs font-medium hover:underline ".concat(getTextClass()) }, { children: "\uD83D\uDD04 Try Again" })) }))), toast.action && (_jsx("div", __assign({ className: "mt-2" }, { children: _jsx("button", __assign({ onClick: function () {
                                    toast.action.onClick();
                                    handleClose();
                                }, className: "text-xs font-medium hover:underline ".concat(getTextClass()) }, { children: toast.action.label })) })))] })), toast.dismissible && (_jsx("button", __assign({ onClick: handleClose, className: "flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity ".concat(getTextClass()) }, { children: "\u2715" })))] })) })));
};
export default ToastProvider;
